import * as React from 'react';
import styled from 'styled-components';
import {
  isBrowser,
  ModuleCategories,
  ModuleCategoriesStyle,
  Appearance,
  CmsModuleType,
  ImageCDNProvider,
} from '@packages/common';
import { is, media, themeGet, breakpointSizes, Button, Box } from '@packages/ui';
import { Translate } from '@packages/i18n';

import { CallToAction } from '../CallToAction/CallToAction';
import { moduleMargin } from '../utils';
import { ListUl, List, ListLi } from '../../List/List';
import { Error } from '../Error/Error';
import { Headline } from '../Headline/Headline';
import { SimpleSlider } from '../../SimpleSlider/SimpleSlider';
import { CategoriesItem } from '../CategoriesItem/CategoriesItem';

export const CategoriesItemWrapper = styled.div<{ isSlider: boolean }>`
  ${is('isSlider')`
    margin-left: ${themeGet.space(3)};
    margin-right: ${themeGet.space(3)};
    ${media.from.lg`
      margin-left: ${themeGet.space(4)};
      margin-right: ${themeGet.space(4)};
    `}

    float: left;
  `}
`;

const ButtonContainer = styled.div`
  display: flex;

  ${media.from.lg`
    display: none;
  `};
`;

const CTAContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: ${themeGet.space(4)} 0;
  position: relative;
`;

export const CategoriesStyled = styled(Box)<{ isCategoryPage: boolean }>`
  ${moduleMargin};
  ${media.from.md`
    padding: 0 12px;
  `}

  ${ListUl} {
    list-style-type: none;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;

    ${ListLi} {
      display: block;
      margin-top: ${themeGet.space(4)};
      margin-left: ${themeGet.space(3)};
      margin-right: ${themeGet.space(3)};
      ${media.to.xl`
        display: flex;
        justify-content: space-around;
      `}

      ${media.from.xxl`
        margin-left: ${themeGet.space(4)};
        margin-right: ${themeGet.space(4)};
      `}
    }
  }
`;

const CategoriesList = styled(List)`
  display: flex;
  flex-wrap: wrap;
  align-content: space-between;
`;

export const CtaLink = styled(Button).attrs({ as: 'a' })`
  border-radius: 6px;
  display: inline-block;
`;

export const StyledButton = styled(Button)`
  margin: 24px auto 0;
`;

export type CategoriesProps = ModuleCategories & {
  onButtonClick?: () => void;
  cdnProvider: ImageCDNProvider;
};

// [<sm, sm-md, md-lg, lg-xl, xl-xxl, >xxl]
const minItemsRequiredToDisplaySlider = [2, 4, 4, 4, 6, 8];

export const Categories: React.FC<CategoriesProps> = (props) => {
  const { title, titleTag, items, onButtonClick, style, cta, showAllCategoryButton, appearance, cdnProvider } = props;
  const [width, setWidth] = React.useState(isBrowser() ? window.innerWidth : 0);

  React.useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  });

  const isCategories = style === ModuleCategoriesStyle.CATEGORIES;
  const appearanceSlider = appearance === Appearance.SLIDER;

  const renderItem = ({ item, index }: any, isSlider = false) => {
    const { style } = props;
    if (item.contentType === CmsModuleType.ERROR) {
      return <Error key={item.id} {...item} />;
    }

    return (
      <CategoriesItemWrapper isSlider={isSlider === true} key={`categoy-item-${item.id}-${index}`}>
        <CategoriesItem {...item} cdnProvider={cdnProvider} showLabel={style === ModuleCategoriesStyle.CATEGORIES} />
      </CategoriesItemWrapper>
    );
  };

  const shouldRenderCarousel = () => {
    if (!appearanceSlider || !isBrowser()) {
      return false;
    }

    const breakpoints = Object.values(breakpointSizes);
    for (let idx = 0; idx < minItemsRequiredToDisplaySlider.length; idx++) {
      const breakpoint = breakpoints[Math.min(idx, breakpoints.length - 1)];
      const breakpointSizeNum = Number(breakpoint.replace('px', ''));
      if (breakpointSizeNum > width) {
        return minItemsRequiredToDisplaySlider[idx] <= items.length;
      }
    }

    return minItemsRequiredToDisplaySlider[minItemsRequiredToDisplaySlider.length - 1] <= items.length;
  };

  return (
    <CategoriesStyled isCategoryPage={isCategories}>
      <Headline as={titleTag} pb={[3, 3, 5]}>
        {title}
      </Headline>
      <div>
        {shouldRenderCarousel() ? (
          <SimpleSlider shownItems={[3, 3, 4, 4, 6, 7]}>
            {items.map((item) => item && renderItem({ item }, true))}
          </SimpleSlider>
        ) : (
          <CategoriesList items={items} renderItem={renderItem} />
        )}
      </div>
      {isCategories && showAllCategoryButton === 'yes' && (
        <ButtonContainer>
          <StyledButton onClick={onButtonClick} data-testid="styled-button">
            <Translate translation="allCategories" />
          </StyledButton>
        </ButtonContainer>
      )}
      {cta && (
        <CTAContainer>
          <CallToAction {...cta} as={CtaLink}>
            {cta.title}
          </CallToAction>
        </CTAContainer>
      )}
    </CategoriesStyled>
  );
};
